import { useEffect, useState } from 'react';
import {
  currentSeason, HEROES, HERO, PASS_TIERS, QUEST_POOL, RANKS, rankFor, WEEKLY_QUEST_POOL, XP_PER_TIER,
  type QuestDef, type QuestStateData,
} from '@aleaces/engine';
import type { GameMode } from '@aleaces/protocol';
import { Portrait } from '../components/Portrait';
import { setFavoriteHero, equipSkin } from '../actions';
import { useProfile, useQuestResets } from '../hooks';
import { owns, passTier } from '../profile';
import { toast } from '../toast';

/** Ticks once a minute so quest countdowns stay current without re-render spam. */
function useNow(): number {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);
  return now;
}

function formatRemaining(ms: number): string {
  if (ms <= 0) return 'gleich';
  const min = Math.ceil(ms / 60_000);
  if (min < 60) return min + ' Min';
  const h = Math.floor(min / 60);
  if (h < 48) return h + ' Std ' + (min % 60) + ' Min';
  return Math.ceil(ms / 86_400_000) + ' Tagen';
}

function QuestPanel({ title, list, pool, resetAt, resetLabel }: {
  title: string;
  list: QuestStateData[];
  pool: QuestDef[];
  resetAt: number | undefined;
  resetLabel: string;
}) {
  const now = useNow();
  return (
    <div className="panel">
      <div className="q-head">
        <div className="eyebrow">{title}</div>
        {resetAt !== undefined && (
          <span className="q-timer" title={resetLabel}>⏳ {resetLabel} in {formatRemaining(resetAt - now)}</span>
        )}
      </div>
      {list.map((q) => {
        const d = pool.find((x) => x.id === q.id);
        if (!d) return null;
        return (
          <div className={'quest' + (q.done ? ' done' : '')} key={q.id}>
            <div className="q-top">
              <b>{d.text}</b>
              <span className="qreward">{d.xp} EP · {d.taler} T</span>
            </div>
            <div className="bar">
              <i style={{ width: (q.progress / d.goal) * 100 + '%', background: q.done ? 'var(--ok)' : undefined }} />
            </div>
            <div className="muted" style={{ fontFamily: 'var(--font-ui)', fontSize: 12 }}>
              {q.done ? 'Erfüllt' : q.progress + ' / ' + d.goal}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function MenuScreen({ onPlay }: { onPlay: (mode: GameMode) => void }) {
  const p = useProfile();
  const resets = useQuestResets();
  if (!p) return null;
  const hero = HERO[p.hero] ?? HEROES[0]!;
  const rk = rankFor(p.rating);
  const next = RANKS[RANKS.indexOf(rk) + 1];
  const season = currentSeason();
  const days = Math.max(0, Math.ceil((season.end.getTime() - Date.now()) / 86400000));
  const tier = passTier(p);

  return (
    <section className="screen">
      <div className="menu-grid">
        <div>
          <div className="eyebrow">Deine Helden</div>
          <h1>Wer sitzt heute mit am Tisch?</h1>
          <p className="muted" style={{ fontFamily: 'var(--font-ui)', fontSize: 13, maxWidth: '64ch', margin: '8px 0 0' }}>
            Im Mehrspieler-Modus wird dir <b style={{ color: 'var(--gold-hi)' }}>nach dem Matchmaking ein zufälliger Held</b> aus
            deinen vorhandenen Helden zugelost – einmal neu ziehen ist erlaubt. Hier verwaltest du Skins und siehst die Heldenkräfte.
          </p>
          <div className="hero-select">
            {HEROES.map((h) => (
              <div
                key={h.id}
                className={'hero-card' + (h.id === p.hero ? ' selected' : '')}
                tabIndex={0}
                role="button"
                aria-pressed={h.id === p.hero}
                aria-label={h.name + ' als Lieblingsheld wählen'}
                onClick={() => setFavoriteHero(h.id)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setFavoriteHero(h.id); }}
              >
                <Portrait heroId={h.id} skinId={p.skins[h.id] ?? 'base'} frame={h.id === p.hero ? p.frame : 'wood'} />
                <div className="hname">{h.name}</div>
                <div className="hrace">{h.race}</div>
                <span className="hstyle">{h.style}</span>
                <div className="hab" title={h.ability.desc}>
                  <b>{h.ability.icon} {h.ability.name}</b>
                  <span>{h.ability.desc}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="skin-row">
            <span className="muted">Skin für {hero.name.split(' ')[0]}:</span>
            {hero.skins.map((s) => {
              const k = hero.id + ':' + s.id;
              const own = owns('skin', k, p);
              const sel = (p.skins[hero.id] ?? 'base') === s.id;
              return (
                <button
                  key={s.id}
                  className={'skin-dot' + (sel ? ' selected' : '') + (own ? '' : ' locked')}
                  title={s.name + (own ? '' : ' (gesperrt)')}
                  aria-label={'Skin ' + s.name + (own ? '' : ' (gesperrt)') + (sel ? ', ausgewählt' : '')}
                  aria-pressed={sel}
                  style={{ background: `linear-gradient(135deg,${s.bg[0]},${s.acc})` }}
                  onClick={() => {
                    if (!own) { toast('Skin gesperrt', 'Freischaltbar über den Battlepass.'); return; }
                    equipSkin(hero.id, s.id);
                  }}
                />
              );
            })}
            <span className="muted">{hero.skins.find((s) => s.id === (p.skins[hero.id] ?? 'base'))?.name}</span>
          </div>
          <div className="play-row">
            <button className="btn gold" onClick={() => onPlay('ranked')}>Ranglistenturnier suchen</button>
            <button className="btn" onClick={() => onPlay('casual')}>Freundschaftsrunde suchen</button>
            <span className="muted" style={{ fontFamily: 'var(--font-ui)', fontSize: 13, maxWidth: 360 }}>
              Sit &amp; Go, 1.500 Chips pro Kopf, steigende Blinds. Ranglisten-Turniere sind 6-handig und zählen zur Wertung;
              Freundschaftsrunden sind 3-handig und schnell. Freie Plätze übernehmen Wirtshaus-Helden.
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="panel season-box">
            <div className="eyebrow">Saison {season.num} · {season.name}</div>
            <div className="rank-badge">
              <div className="rank-shield" style={{ background: rk.color }}>{rk.name[0]}</div>
              <div>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, color: 'var(--parch)' }}>{rk.name}</div>
                <div className="muted" style={{ fontFamily: 'var(--font-ui)', fontSize: 13 }}>
                  {p.rating} Punkte{next ? ' · ' + (next.min - p.rating) + ' bis ' + next.name : ' · Höchster Rang'}
                </div>
              </div>
            </div>
            <div className="bar"><i style={{ width: (next ? ((p.rating - rk.min) / (next.min - rk.min)) * 100 : 100) + '%' }} /></div>
            <div className="muted" style={{ fontFamily: 'var(--font-ui)', fontSize: 13 }}>
              Die Saison endet in <b style={{ color: 'var(--parch)' }}>{days} Tagen</b>. Dein Rang wird dann in Saisonbelohnungen
              umgewandelt und leicht zurückgesetzt.
            </div>
          </div>
          <QuestPanel title="Tagesquests" list={p.quests.list} pool={QUEST_POOL} resetAt={resets?.daily} resetLabel="Neue Quests" />
          <QuestPanel title="Wochenquests" list={p.weeklyQuests.list} pool={WEEKLY_QUEST_POOL} resetAt={resets?.weekly} resetLabel="Reset" />
          <div className="panel">
            <div className="eyebrow">Battlepass</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontFamily: 'var(--font-ui)' }}>
              <span>Stufe <b style={{ color: 'var(--gold-hi)' }}>{tier}</b> / {PASS_TIERS}</span>
              <span className="muted" style={{ fontSize: 12 }}>{p.premium ? 'Premium aktiv' : 'Gratis-Track'}</span>
            </div>
            <div className="bar" style={{ margin: '8px 0' }}>
              <i style={{ width: Math.min(100, ((p.passXP % XP_PER_TIER) / XP_PER_TIER) * 100) + '%' }} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
